import { inject } from 'inversify';
import type { KyInstance, Options } from 'ky';
import { Injectable } from '../../decorators/injectable';
import { type IKyClientService, KyClientService } from '../ky-client/ky-client.service';

export interface IHttpService {
  get<T>(url: string, options?: Options): Promise<T>;
  getBlob(url: string, options?: Options): Promise<Response>;
  post<T>(url: string, data?: unknown, options?: Options): Promise<T>;
  postBlob(url: string, data?: unknown, options?: Options): Promise<Response>;
  postForm<T>(url: string, formData: FormData, options?: Options): Promise<T>;
  putForm<T>(url: string, formData: FormData, options?: Options): Promise<T>;
  put<T>(url: string, data?: unknown, options?: Options): Promise<T>;
  patch<T>(url: string, data?: unknown, options?: Options): Promise<T>;
  delete<T = void>(url: string, options?: Options): Promise<T>;
}

// ponytail: ky 2.0 dropped its empty-body short-circuit — .json() now runs
// JSON.parse('') and throws SyntaxError. Nest controllers that return
// Promise<void> respond 200 OK with empty body (PATCH /team/:id/deactivate,
// POST /lots/qr-bind-batch, etc.), so every method must treat empty body as
// undefined. Supersedes the 204-only guard that lived on delete.
async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return (text === '' ? undefined : JSON.parse(text)) as T;
}

@Injectable()
class HttpService implements IHttpService {
  private readonly client: KyInstance;

  constructor(@inject(KyClientService) private readonly kyClientService: IKyClientService) {
    this.client = this.kyClientService.getClient();
  }

  async get<T>(url: string, options?: Options): Promise<T> {
    return parseBody<T>(await this.client.get(url, options));
  }

  async getBlob(url: string, options?: Options): Promise<Response> {
    return this.client.get(url, options);
  }

  async post<T>(url: string, data?: unknown, options?: Options): Promise<T> {
    return parseBody<T>(await this.client.post(url, { json: data, ...options }));
  }

  async postBlob(url: string, data?: unknown, options?: Options): Promise<Response> {
    return this.client.post(url, { json: data, ...options });
  }

  async postForm<T>(url: string, formData: FormData, options?: Options): Promise<T> {
    return parseBody<T>(
      await this.client.post(url, { body: formData, timeout: 120_000, ...options }),
    );
  }

  async putForm<T>(url: string, formData: FormData, options?: Options): Promise<T> {
    return parseBody<T>(
      await this.client.put(url, { body: formData, timeout: 120_000, ...options }),
    );
  }

  async put<T>(url: string, data?: unknown, options?: Options): Promise<T> {
    return parseBody<T>(await this.client.put(url, { json: data, ...options }));
  }

  async patch<T>(url: string, data?: unknown, options?: Options): Promise<T> {
    return parseBody<T>(await this.client.patch(url, { json: data, ...options }));
  }

  async delete<T = void>(url: string, options?: Options): Promise<T> {
    return parseBody<T>(await this.client.delete(url, options));
  }
}

export { HttpService };
